import React, { useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Button, Card, Col, Layout, Row, Typography } from "antd";
import {
  CheckCircleOutlined,
  FilePdfOutlined,
  SendOutlined,
  WhatsAppOutlined,
} from "@ant-design/icons";
import CustomHeader from "../Header";
import Footer from "../Footer";
import ServiceCardScroll from "./ServiceCardScroll";
import "./ServiceFeature.css";
import {
  serviceAdvantages,
  serviceDashboardFeatures,
  serviceFeaturedCards,
  serviceFeatureText,
  serviceTargetAudience,
  serviceTemplates,
} from "./serviceFeatureConstant";
import { scrollToElementById } from "../../../Utility/scrollUtils";
import { Helmet } from "react-helmet-async";

const { Header, Content } = Layout;
const { Title, Paragraph } = Typography;

const ServiceFeature: React.FC = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    scrollToElementById("service");
  }, [pathname]);

  return (
    <div id="service">
      <Helmet>
        <title>Service Management Software for Smarter Operations | Ease Margin</title>
        <meta
          name="description"
          content="Manage service operations efficiently with service management software. Track requests, assign tasks, monitor performance, and improve service delivery easily."
        />
        <link
          rel="canonical" href="https://easemargin.com/services/service-management" />
      </Helmet>

      <CustomHeader />
      <Layout className="serviceFeature-layout">
        <Header className="serviceFeature-header">
          <div className="serviceFeature-header-inner">
            <Title level={1} className="serviceFeature-title-large">
              {serviceFeatureText.HERO_TITLE}
            </Title>
            <Paragraph className="serviceFeature-paragraph-header">
              {serviceFeatureText.HERO_DESCRIPTION}
            </Paragraph>
            <Link to="/requestdemo">
              <Button type="primary" size="large" className="btn-orange">
                Request a Demo
              </Button>
            </Link>
          </div>
        </Header>
        <Content className="serviceFeature-content">
          <Title level={2} className="serviceFeature-title-2nd">
            {serviceFeatureText.INTRO_TITLE}
          </Title>
          <Paragraph className="serviceFeature-paragraph-main">
            {serviceFeatureText.INTRO_DESCRIPTION}
          </Paragraph>
          <Title level={3} className="serviceFeature-title-3rd">
            {serviceFeatureText.SERVICE_FLOW_TITLE}
          </Title>
          <ServiceCardScroll />
          <Title level={3} className="serviceFeature-title-3rd-marginTop">
            {serviceFeatureText.MULTI_FORMAT_TITLE}
          </Title>
          <Row gutter={[24, 24]} style={{ marginTop: 20 }}>
            <Col xs={24} md={12}>
              <Card bordered={false} className="card-default">
                <Title level={4} style={{ color: "#722ed1" }}>
                  {serviceFeatureText.DOC_FORMAT}
                </Title>
                <Row gutter={[24, 24]}>
                  <Col span={12}>
                    <Paragraph style={{ marginLeft: 20 }}>
                      <FilePdfOutlined
                        style={{ marginRight: 8, color: "#fa541c" }}
                      />
                      PDF
                    </Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      🖨️ Printable A4
                    </Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      ✉️ Email-ready attachments
                    </Paragraph>
                  </Col>
                </Row>
              </Card>
            </Col>
            <Col xs={24} md={12}>
              <Card bordered={false} className="card-default">
                <Title level={4} style={{ color: "#722ed1" }}>
                  {serviceFeatureText.SHARING_STORE}
                </Title>
                <Row gutter={[0, 24]}>
                  <Col span={12}>
                    <Paragraph style={{ marginLeft: 20 }}>
                      <FilePdfOutlined
                        style={{ marginRight: 8, color: "#f5222d" }}
                      />
                      Download
                    </Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      🖨️ Print directly
                    </Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      <SendOutlined
                        style={{ marginRight: 8, color: "#1890ff" }}
                      />
                      Send via Email
                    </Paragraph>
                  </Col>
                  <Col span={12}>
                    <Paragraph style={{ marginLeft: 20 }}>
                      <WhatsAppOutlined
                        style={{ marginRight: 8, color: "#25D366" }}
                      />
                      Send via WhatsApp
                    </Paragraph>
                    <Paragraph style={{ marginLeft: 20 }}>
                      🔗 Share Document Links
                    </Paragraph>
                  </Col>
                </Row>
              </Card>
            </Col>
          </Row>
          <Row gutter={[24, 24]} style={{ marginTop: 40 }}>
            {serviceTemplates.map((feature, index) => (
              <Col xs={24} md={12} key={index}>
                <Card bordered={false} className="card-default">
                  <Title level={4} className="serviceFeature-title-flex">
                    <span style={{ marginRight: 8 }}>{feature.icon}</span>
                    {feature.title}
                  </Title>
                  <Paragraph className="serviceFeature-paragraph-desc">
                    {feature.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
          <Title level={3} className="serviceFeature-title-3rd-marginTop">
            {serviceFeatureText.DASHBOARD_TITLE}
          </Title>
          <Row gutter={[24, 24]} style={{ marginBottom: 40 }}>
            {serviceDashboardFeatures.map((feature, index) => (
              <Col xs={24} sm={12} key={index}>
                <Card
                  bordered={false}
                  className="card-default"
                  style={{ backgroundColor: "#F6FFED" }}
                >
                  <Paragraph className="serviceFeature-paragraph-dashboard">
                    <CheckCircleOutlined
                      style={{ color: "#1890ff", marginRight: 8 }}
                    />
                    {feature}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>

          <Row gutter={[24, 24]} style={{ marginTop: 40 }}>
            {serviceFeaturedCards.map((feature, index) => (
              <Col xs={24} md={12} key={index}>
                <Card bordered={false} className="card-default">
                  <Title level={4} className="purchaseFeature-title-flex">
                    <span style={{ marginRight: 8 }}>{feature.icon}</span>
                    {feature.title}
                  </Title>
                  <Paragraph className="purchaseFeature-paragraph-desc">
                    {feature.description}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>

          <Title level={3} className="serviceFeature-title-3rd-marginTop">
            {serviceFeatureText.ADVANTAGE_TITLE}
          </Title>
          <Row gutter={[16, 16]} style={{ marginBottom: 40 }}>
            {serviceAdvantages.map((advantage, idx) => (
              <Col xs={24} sm={12} key={idx}>
                <Card
                  bordered={false}
                  className="card-default card-light"
                  style={{ backgroundColor: "#F6FFED" }}
                >
                  <Paragraph className="serviceFeature-paragraph-dashboard">
                    <CheckCircleOutlined
                      style={{ color: "#1890ff", marginRight: 8 }}
                    />
                    {advantage}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
          <Title level={3} className="serviceFeature-title-3rd-marginTop">
            {serviceFeatureText.PERFECT_FOR_TITLE}
          </Title>
          <Row gutter={[16, 16]}>
            {serviceTargetAudience.map((target, idx) => (
              <Col xs={24} sm={12} key={idx}>
                <Card
                  bordered={false}
                  className="card-default card-light"
                  style={{ backgroundColor: "#FFF1F0" }}
                >
                  <Paragraph className="serviceFeature-paragraph-targetAudience">
                    <CheckCircleOutlined
                      style={{ color: "#1890ff", marginRight: 8 }}
                    />
                    {target}
                  </Paragraph>
                </Card>
              </Col>
            ))}
          </Row>
          <Card
            bordered={false}
            className="card-default serviceFeature-card-footer"
          >
            <Title level={2} className="serviceFeature-title-footer">
              {serviceFeatureText.CTA_TITLE}
            </Title>
            <Paragraph className="serviceFeature-paragraph-footer">
              {serviceFeatureText.CTA_DESCRIPTION}
            </Paragraph>
            <Link to="/requestdemo">
              <Button type="primary" size="large" className="btn-orange">
                {serviceFeatureText.CTA_BUTTON}
              </Button>
            </Link>
          </Card>
        </Content>
      </Layout>
      <Footer />
    </div>
  );
};

export default ServiceFeature;
